<template>
  <div class="chat-sessions-page">
    <div class="sessions-section">
      <div class="sessions-header">
        <h2 class="sessions-page-title">Chat Sessions</h2>
        <BaseButton
          stylevariant="primary"
          :cus_style="{ width: 'auto', padding: '0.5rem 0.5rem', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
          @click="createNewChat"
        >
          <i class="las la-plus"></i>
          New Chat
        </BaseButton>
      </div>

      <div v-if="loading" class="loading-state">
        <BaseSpinner />
      </div>
      <div v-else-if="error" class="error-message">{{ error }}</div>
      <div v-else-if="chatSessions.length === 0" class="no-sessions">
        <div class="empty-state">
          <i class="las la-comments"></i>
          <h5>No Chat Sessions Yet</h5>
          <p>Start your first conversation with this candidate by clicking "New Chat" above.</p>
        </div>
      </div>
      <div v-else class="sessions-list">
        <div
          v-for="session in chatSessions"
          :key="session.id"
          class="session-item"
          @click="selectSession(session)"
        >
          <div class="session-info">
            <div class="session-title">
              {{ session.chat_header || `Chat Session ${session.id}` }}
            </div>
          </div>
          <div class="session-actions">
            <i class="las la-arrow-right session-arrow" aria-hidden="true"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service';
import BaseButton from '@/components/UI/BaseButton.vue';
import BaseSpinner from '@/components/UI/BaseSpinner.vue';

export default {
  name: 'ChatSessionsPage',
  components: {
    BaseButton,
    BaseSpinner
  },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      chatSessions: [],
      loading: true,
      error: null
    };
  },
  async mounted() {
    await this.loadChatSessions();
  },
  methods: {
    async loadChatSessions() {
      if (!this.candidateId) {
        this.error = 'No candidate selected';
        this.loading = false;
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        const sessions = await ChatbotService.getChatSessions(this.candidateId);
        this.chatSessions = Array.isArray(sessions)
          ? sessions
          : (sessions.sessions || sessions.data || []);
      } catch (err) {
        this.error = 'Failed to load chat sessions.';
        console.error('Error loading chat sessions:', err);
      } finally {
        this.loading = false;
      }
    },

    selectSession(session) {
      this.$router.push({
        name: 'chatbot-chat-session',
        params: {
          candidateId: this.candidateId,
          sessionId: session.id
        }
      });
    },

    async createNewChat() {
      try {
        const payload = {
          candidate_id: this.candidateId
        };

        const session = await ChatbotService.createChatSession(this.candidateId, payload);

        this.$router.push({
          name: 'chatbot-chat-session',
          params: {
            candidateId: this.candidateId,
            sessionId: session.id || session.session_id
          }
        });
      } catch (err) {
        console.error('Error creating chat session:', err);
        this.$bvToast.toast('Failed to create chat session. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.chat-sessions-page {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.sessions-section {
  flex: 1;
  padding: 1.5rem 2rem 2rem;
  overflow-y: auto;
  background: #ffffff;
}

.sessions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2.5rem;
}

.sessions-page-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem 2rem;
}

.no-sessions {
  text-align: center;
  color: #718096;
  padding: 3rem 2rem;
  font-family: $brand-font-regular;
}

.error-message {
  text-align: center;
  color: #e53e3e;
  padding: 1rem 2rem;
  font-size: 0.9rem;
  font-family: $brand-font-regular;
  background: #fed7d7;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  margin: 1rem 0;
}

.empty-state {
  text-align: center;

  i {
    font-size: 4rem;
    color: #a0aec0;
    margin-bottom: 1.5rem;
    display: block;
  }

  h5 {
    margin: 0 0 0.75rem 0;
    color: #2d3748;
    font-size: 1.2rem;
  }

  p {
    margin: 0;
    color: #718096;
    font-size: 1rem;
  }
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.session-item {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1.25rem;

  &:hover {
    background: #edf2f7;
    border-color: $color-primary;

    .session-arrow {
      transform: translateX(4px);
      color: $color-primary;
    }
  }
}

.session-info {
  flex: 1;
  min-width: 0;
}

.session-title {
  position: relative;
  top: 3px;
  margin-left: 0.5rem;
  font-size: 1.1rem;
  font-weight: 500;
  color: #2d3748;
  font-family: $brand-font-medium;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.session-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.session-arrow {
  font-size: 1.25rem;
  color: #718096;
  transition: transform 0.2s ease, color 0.2s ease;
}

@media (max-width: 768px) {
  .sessions-section {
    padding: 1.5rem;
  }
}
</style>
